import { Component, Fragment } from 'react';
import type { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { ChatChart } from './viz/ChatChart';
import type { ChatChartSpec } from './viz/ChatChart';
import { ChatDataTable } from './viz/ChatDataTable';
import type { ChatTableAlign } from './viz/ChatDataTable';
import { ChatLink } from './viz/ChatLink';
import { ChatStatTiles } from './viz/ChatStatTiles';
import type { ChatStatTile } from './viz/ChatStatTiles';

/**
 * Markdown for assistant answers, with dashboard render blocks.
 *
 * Deliberately not a markdown library: the block dispatch, the streaming degradation
 * states and the link policy are all custom, so a library would be wrapped away to
 * nothing. The supported surface is what the assistant is told to emit — headings,
 * bold/italic/code, links, bullets, numbered steps, rules, blockquotes — plus:
 *
 *   ```kpi     -> ChatStatTiles   (JSON array of tiles)
 *   ```chart   -> ChatChart       (JSON chart spec)
 *   ```table   -> ChatDataTable   (JSON {title, headers, rows})
 *
 * and plain GitHub-flavoured tables, which previously folded into a paragraph and
 * leaked raw `| :--- |` pipes at the operator.
 *
 * Replies can arrive part-written (a retried turn, a truncated answer), so a fence is
 * frequently incomplete. Three degradation states, and never a fourth: an unterminated
 * fence shows a pending card, a closed-but-unparseable one falls back to a code block
 * so nothing is silently swallowed, and a spec that throws at render is caught by a
 * boundary — without one, a single bad chart unmounts the whole transcript.
 *
 * See `RENDER_BLOCKS_SPEC.md` in this folder: it is the other half of this contract and
 * must change in the same commit.
 */

const VISUAL_LANGS = new Set(['kpi', 'chart', 'table']);

const LINK_PART = /^\[([^\]\n]*)\]\(([^)\s]*)\)$/;
const RULE_RE = /^\s*([-*_])\1{2,}\s*$/;

const CODE_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * Inline formatting.
 *
 * `String.split` takes the leftmost match, so a link inside a code span still loses to
 * the backtick; the alternation order only breaks ties at the same index. A link that
 * is still half-written (`[APP-1](/applica`) has no closing paren, so it renders
 * literally — exactly what `**bold**` already does mid-stream.
 */
function renderInline(text: string, keyBase: string, depth = 0): ReactNode[] {
  const pattern =
    depth === 0
      ? /(\[[^\]\n]*\]\([^)\s]*\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|_[^_]+_)/g
      : /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|_[^_]+_)/g;

  return text
    .split(pattern)
    .filter((p) => p !== '')
    .map((part, i) => {
      const key = `${keyBase}-${i}`;
      const link = depth === 0 ? LINK_PART.exec(part) : null;
      if (link) {
        // Recurse for the label so [**APP-1**](/applications/APP-1) still bolds. The
        // label pattern excludes `]`, so a nested link cannot match; depth guards anyway.
        return (
          <ChatLink key={key} href={link[2]}>
            {renderInline(link[1], key, depth + 1)}
          </ChatLink>
        );
      }
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <Box component="strong" key={key} sx={{ fontWeight: 700 }}>
            {part.slice(2, -2)}
          </Box>
        );
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <Box
            component="code"
            key={key}
            sx={{
              fontFamily: CODE_FONT,
              fontSize: '0.85em',
              px: 0.5,
              py: 0.15,
              borderRadius: '5px', // literal px: `sx.borderRadius` is theme-multiplied
              wordBreak: 'break-all',
              bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.06 : 0.14),
            }}
          >
            {part.slice(1, -1)}
          </Box>
        );
      }
      if (
        (part.startsWith('*') && part.endsWith('*')) ||
        (part.startsWith('_') && part.endsWith('_'))
      ) {
        return (
          <Box component="em" key={key} sx={{ fontStyle: 'italic' }}>
            {part.slice(1, -1)}
          </Box>
        );
      }
      return <Fragment key={key}>{part}</Fragment>;
    });
}

type Block =
  | { type: 'h1' | 'h2' | 'h3'; lines: string[] }
  | { type: 'p' | 'quote' | 'ul' | 'ol'; lines: string[] }
  | { type: 'hr' }
  | { type: 'code'; lang: string; lines: string[] }
  | { type: 'visual'; lang: string; source: string; closed: boolean }
  | { type: 'table'; headers: string[]; rows: string[][]; align: ChatTableAlign[] };

const isTableRow = (l: string) => l.includes('|');
const isTableSep = (l: string) =>
  /^\s*\|?[\s]*:?-{2,}:?[\s]*(\|[\s]*:?-{2,}:?[\s]*)*\|?\s*$/.test(l);

/** Split a GFM row into cells, tolerating optional outer pipes. */
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function alignFromSep(line: string): ChatTableAlign[] {
  return splitRow(line).map((c) => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

export function parseChatBlocks(src: string): Block[] {
  const lines = String(src || '')
    .replace(/\r\n/g, '\n')
    .split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'p', lines: [para.join('\n')] });
      para = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();

    // ── fenced blocks (visual or code) ───────────────────────────────────────
    // Deliberately loose. The model gets this slightly wrong often enough that a strict
    // fence rule is how a wall of raw JSON reaches the operator: an indented fence, a
    // `CHART` tag in caps, `~~~` instead of backticks, or a note after the tag
    // (```chart (last 30 days)) must all still open a visual.
    const fence = line.match(/^[ \t]*(?:`{3,}|~{3,})[ \t]*([A-Za-z][\w+-]*)?[^\n]*$/);
    if (fence) {
      flushPara();
      const lang = (fence[1] || '').toLowerCase();
      const body: string[] = [];
      let closed = false;
      let j = i + 1;
      for (; j < lines.length; j++) {
        if (/^[ \t]*(?:`{3,}|~{3,})[ \t]*$/.test(lines[j])) {
          closed = true;
          break;
        }
        body.push(lines[j]);
      }
      // An unterminated fence consumes the rest of the message rather than
      // re-parsing half-written JSON as prose.
      i = closed ? j : lines.length;
      if (VISUAL_LANGS.has(lang)) {
        blocks.push({ type: 'visual', lang, source: body.join('\n'), closed });
      } else {
        blocks.push({ type: 'code', lang, lines: body });
      }
      continue;
    }

    if (!line.trim()) {
      flushPara();
      continue;
    }

    if (RULE_RE.test(line)) {
      flushPara();
      blocks.push({ type: 'hr' });
      continue;
    }

    // ── GFM table: a row followed by a delimiter row ─────────────────────────
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushPara();
      const headers = splitRow(line);
      const align = alignFromSep(lines[i + 1]);
      const rows: string[][] = [];
      let j = i + 2;
      for (; j < lines.length; j++) {
        const r = lines[j].trimEnd();
        if (!r.trim() || !isTableRow(r)) break;
        const cells = splitRow(r);
        // Pad short rows rather than dropping them: a truncated answer produces
        // short rows, and dropping them makes data disappear.
        while (cells.length < headers.length) cells.push('');
        rows.push(cells.slice(0, headers.length));
      }
      i = j - 1;
      blocks.push({
        type: 'table',
        headers,
        rows,
        align: headers.map((_, k) => align[k] ?? 'left'),
      });
      continue;
    }

    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara();
      const level = heading[1].length;
      blocks.push({ type: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3', lines: [heading[2]] });
      continue;
    }

    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'quote') last.lines.push(quote[1]);
      else blocks.push({ type: 'quote', lines: [quote[1]] });
      continue;
    }

    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    if (ul) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'ul') last.lines.push(ul[1]);
      else blocks.push({ type: 'ul', lines: [ul[1]] });
      continue;
    }

    const ol = line.match(/^\s*\d{1,3}[.)]\s+(.*)$/);
    if (ol) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'ol') last.lines.push(ol[1]);
      else blocks.push({ type: 'ol', lines: [ol[1]] });
      continue;
    }

    para.push(line.trim());
  }

  flushPara();
  return blocks;
}

/**
 * The JSON inside a visual fence is model-generated, so a shape that type-checks but
 * breaks at render time is a live possibility. Without a boundary a single throw
 * unmounts the whole transcript and the panel goes blank, so each visual is isolated
 * and degrades to its raw source instead.
 */
class VisualBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('Loan ops assistant: visual failed to render', error);
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function CodeBlock({ lang, lines }: { lang: string; lines: string[] }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        mt: 1.25,
        mb: 1.25,
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px', // literal px: `sx.borderRadius` is theme-multiplied
        overflowX: 'auto',
        fontFamily: CODE_FONT,
        fontSize: '0.72rem',
        lineHeight: 1.55,
        bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.04 : 0.1),
      }}
    >
      {lang && (
        <Box
          component="span"
          sx={{
            display: 'block',
            mb: 0.5,
            fontFamily: 'inherit',
            fontSize: '0.62rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {lang}
        </Box>
      )}
      <code>{lines.join('\n')}</code>
    </Box>
  );
}

function VisualPending({ lang }: { lang: string }) {
  const what = lang === 'kpi' ? 'metrics' : lang === 'chart' ? 'chart' : 'table';
  return (
    <Box
      role="status"
      sx={{
        my: 1.25,
        px: 1.5,
        py: 1.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: '10px', // literal px: `sx.borderRadius` is theme-multiplied
        color: 'text.secondary',
        fontSize: '0.78rem',
      }}
    >
      <CircularProgress size={13} thickness={5} aria-hidden />
      {`Building ${what}…`}
    </Box>
  );
}

function VisualError({ lang, source }: { lang: string; source: string }) {
  return (
    <Box
      sx={{
        my: 1.25,
        p: 1.25,
        border: '1px solid',
        borderColor: (t) => alpha(t.palette.warning.main, 0.5),
        borderRadius: '10px', // literal px: `sx.borderRadius` is theme-multiplied
        bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === 'light' ? 0.08 : 0.16),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          fontWeight: 700,
          color: 'text.primary',
        }}
      >
        <ReportProblemRoundedIcon aria-hidden sx={{ fontSize: '0.9rem' }} />
        {`Couldn't render this ${lang}`}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          overflowX: 'auto',
          fontFamily: CODE_FONT,
          fontSize: '0.7rem',
          lineHeight: 1.5,
          color: 'text.secondary',
        }}
      >
        <code>{source}</code>
      </Box>
    </Box>
  );
}

/** The JSON payload, or the prose the model wrapped around it. */
function sliceJson(text: string): string | null {
  const open = text.search(/[{[]/);
  if (open < 0) return null;
  const closeCurly = text.lastIndexOf('}');
  const closeSquare = text.lastIndexOf(']');
  const close = Math.max(closeCurly, closeSquare);
  if (close <= open) return null;
  return text.slice(open, close + 1);
}

/**
 * Parse a visual payload, tolerating the near-misses the model actually makes.
 *
 * The prompt half asks for clean JSON and is being tightened, but this is the layer that
 * decides whether a slip is invisible or a wall of raw JSON in the operator's face, so
 * it forgives a nested ```json fence, a sentence either side of the object, a trailing
 * comma and smart quotes — and nothing else. Anything it still cannot parse falls
 * through to the existing graceful degradation.
 */
export function parseVisualJson(text: string): { ok: true; value: unknown } | { ok: false } {
  const raw = String(text ?? '').trim();
  if (!raw) return { ok: false };

  const candidates: string[] = [];
  const add = (s: string | null) => {
    const t = (s ?? '').trim();
    if (t && !candidates.includes(t)) candidates.push(t);
  };

  add(raw);
  const unfenced = raw.replace(/^(?:`{3,}|~{3,})[\w+-]*[ \t]*\n?/, '').replace(/\n?(?:`{3,}|~{3,})[ \t]*$/, '');
  add(unfenced);
  const sliced = sliceJson(unfenced);
  add(sliced);
  if (sliced) {
    add(
      sliced
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/,(\s*[}\]])/g, '$1'),
    );
  }

  for (const candidate of candidates) {
    try {
      return { ok: true, value: JSON.parse(candidate) };
    } catch {
      // Try the next repair.
    }
  }
  return { ok: false };
}

/** Render one visual fence, degrading safely on partial or malformed JSON. */
function Visual({ lang, source, closed }: { lang: string; source: string; closed: boolean }) {
  const text = source.trim();
  if (!text) return closed ? null : <VisualPending lang={lang} />;

  const attempt = parseVisualJson(text);
  if (!attempt.ok) {
    // Still arriving -> placeholder. Finished but broken -> show the source, so
    // nothing the assistant said is silently swallowed.
    return closed ? <CodeBlock lang={lang} lines={source.split('\n')} /> : <VisualPending lang={lang} />;
  }
  const parsed = attempt.value;

  try {
    if (lang === 'kpi') {
      const tiles = (Array.isArray(parsed) ? parsed : (parsed as { tiles?: unknown })?.tiles) as
        | ChatStatTile[]
        | undefined;
      if (!Array.isArray(tiles)) throw new Error('kpi expects an array of tiles');
      return <ChatStatTiles tiles={tiles} />;
    }

    if (lang === 'chart') {
      const spec = parsed as ChatChartSpec;
      if (!spec || typeof spec !== 'object' || !Array.isArray(spec.data)) {
        throw new Error('chart expects an object with a data array');
      }
      return <ChatChart spec={spec} />;
    }

    const t = parsed as { title?: string; headers?: unknown; rows?: unknown };
    if (!Array.isArray(t?.headers) || !Array.isArray(t?.rows)) {
      throw new Error('table expects {headers, rows}');
    }
    return (
      <ChatDataTable
        title={typeof t.title === 'string' ? t.title : undefined}
        headers={t.headers.map((h) => String(h ?? ''))}
        rows={t.rows.map((r) =>
          Array.isArray(r) ? r.map((c) => String(c ?? '')) : [String(r ?? '')],
        )}
      />
    );
  } catch {
    return <CodeBlock lang={lang} lines={source.split('\n')} />;
  }
}

/**
 * True when a message contains a visual — a fenced dashboard block or a GFM table.
 *
 * A bubble carrying a chart or a table needs more width than a prose bubble, and this
 * is cheap enough to run on every message on every render.
 */
export function hasChatVisual(text: string): boolean {
  const s = String(text || '');
  // Same tolerance as the parser: indented, tilde-fenced and upper-cased tags count.
  return (
    /^[ \t]*(?:`{3,}|~{3,})[ \t]*(kpi|chart|table)\b/im.test(s) ||
    /\|.*\n\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?/.test(s)
  );
}

const listSx = { my: 0.5, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.25 } as const;

export function ChatMarkdown({ content }: { content: string }) {
  const blocks = parseChatBlocks(content || '');

  return (
    <Box sx={{ lineHeight: 1.55, minWidth: 0 }}>
      {blocks.map((b, i) => {
        const key = `md-${i}`;

        if (b.type === 'visual') {
          return (
            <VisualBoundary key={key} fallback={<VisualError lang={b.lang} source={b.source} />}>
              <Visual lang={b.lang} source={b.source} closed={b.closed} />
            </VisualBoundary>
          );
        }

        if (b.type === 'code') return <CodeBlock key={key} lang={b.lang} lines={b.lines} />;

        if (b.type === 'table') {
          return <ChatDataTable key={key} headers={b.headers} rows={b.rows} align={b.align} />;
        }

        if (b.type === 'hr') {
          return <Box key={key} aria-hidden sx={{ my: 1, borderTop: 1, borderColor: 'divider' }} />;
        }

        if (b.type === 'h1' || b.type === 'h2' || b.type === 'h3') {
          return (
            <Box
              key={key}
              component="p"
              sx={{
                m: 0,
                mt: i === 0 ? 0 : 1.25,
                mb: 0.35,
                fontWeight: 800,
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
                fontSize: b.type === 'h3' ? '0.875rem' : '0.95rem',
              }}
            >
              {renderInline(b.lines[0], key)}
            </Box>
          );
        }

        if (b.type === 'quote') {
          return (
            <Box
              key={key}
              component="blockquote"
              sx={{
                m: 0,
                my: 0.75,
                pl: 1.25,
                borderLeft: '2px solid',
                borderColor: 'divider',
                color: 'text.secondary',
              }}
            >
              {b.lines.map((l, j) => (
                <Box key={`${key}-${j}`} component="p" sx={{ m: 0 }}>
                  {renderInline(l, `${key}-${j}`)}
                </Box>
              ))}
            </Box>
          );
        }

        if (b.type === 'ul' || b.type === 'ol') {
          return (
            <Box
              key={key}
              component={b.type}
              sx={{ ...listSx, listStyleType: b.type === 'ul' ? 'disc' : 'decimal' }}
            >
              {b.lines.map((l, j) => (
                <Box key={`${key}-${j}`} component="li" sx={{ pl: 0.25 }}>
                  {renderInline(l, `${key}-${j}`)}
                </Box>
              ))}
            </Box>
          );
        }

        return (
          <Box key={key} component="p" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderInline(b.lines[0], key)}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Back-compatible entry point. Kept because the panel (and any future surface that
 * shows an assistant answer) renders markdown by calling a function, not by mounting
 * a component.
 */
export function renderChatMarkdown(text: string): ReactNode {
  return <ChatMarkdown content={text} />;
}
